import { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Plus, Trash2, Download, Upload, FileSpreadsheet, ChevronRight, ChevronDown, TrendingUp, AlertTriangle, FolderOpen } from 'lucide-react';

const HEALTH = ['green', 'amber', 'red'];
const healthDot = h => <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: { green: '#2D8B5E', amber: '#BA7517', red: '#D4726A' }[h] || '#9CA3AF' }} />;
const fmt = v => { if (!v) return '0'; v = Number(v); if (v >= 10000000) return `${(v/10000000).toFixed(1)}Cr`; if (v >= 100000) return `${(v/100000).toFixed(1)}L`; return v.toLocaleString('en-IN'); };

export default function ClientTab({ divisionId }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState({}); // clientId → bool
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    client_name: '', entity: '', relationship_start_date: '', annual_value: '',
    services: '', relationship_owner: '', nps_score: '', satisfaction_indicator: 'green',
    how_they_found_us: '', why_they_stay: '', depth_score: '', last_meeting_date: '',
    key_contact_name: '', key_contact_role: '', key_contact_notes: '', notes: '', division_id: divisionId,
  });

  const refresh = async () => {
    const r = await api.get(`/clients-registry?division_id=${divisionId}`);
    setClients(r.data);
  };
  useEffect(() => { refresh().finally(() => setLoading(false)); }, [divisionId]);

  // Concentration analysis: what % of the book sits with the top 5 / top 1 clients?
  const concentration = useMemo(() => {
    const ranked = [...clients]
      .map(c => ({ name: c.client_name, value: Number(c.annual_value) || 0 }))
      .filter(c => c.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = ranked.reduce((s, c) => s + c.value, 0);
    if (total <= 0) return null;
    const top5 = ranked.slice(0, 5);
    const top5Sum = top5.reduce((s, c) => s + c.value, 0);
    const top5Pct = Math.round((top5Sum / total) * 100);
    const topOne = ranked[0];
    const topOnePct = topOne ? Math.round((topOne.value / total) * 100) : 0;
    const risk = top5Pct >= 75 ? 'red' : top5Pct >= 60 ? 'amber' : 'green';
    return { total, top5, top5Sum, top5Pct, topOne, topOnePct, risk, totalClients: ranked.length };
  }, [clients]);

  const resetForm = () => {
    setForm({ client_name: '', entity: '', relationship_start_date: '', annual_value: '', services: '', relationship_owner: '', nps_score: '', satisfaction_indicator: 'green', how_they_found_us: '', why_they_stay: '', depth_score: '', last_meeting_date: '', key_contact_name: '', key_contact_role: '', key_contact_notes: '', notes: '', division_id: divisionId });
    setEditId(null); setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) await api.put(`/clients-registry/${editId}`, form);
    else await api.post('/clients-registry', form);
    resetForm(); refresh();
  };

  const handleEdit = (c) => {
    setForm({ client_name: c.client_name || '', entity: c.entity || '', relationship_start_date: c.relationship_start_date || '', annual_value: c.annual_value || '', services: c.services || '', relationship_owner: c.relationship_owner || '', nps_score: c.nps_score || '', satisfaction_indicator: c.satisfaction_indicator || 'green', how_they_found_us: c.how_they_found_us || '', why_they_stay: c.why_they_stay || '', depth_score: c.depth_score || '', last_meeting_date: c.last_meeting_date || '', key_contact_name: c.key_contacts?.[0]?.name || '', key_contact_role: c.key_contacts?.[0]?.role || '', key_contact_notes: c.key_contacts?.[0]?.notes || '', notes: c.notes || '', division_id: divisionId });
    setEditId(c.id); setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('division_id', divisionId);
    try { const r = await api.post('/clients-registry/upload-csv', fd); alert(`Imported ${r.data.imported} clients${r.data.errors?.length ? `. Errors: ${r.data.errors.join(', ')}` : ''}`); refresh(); }
    catch (err) { alert('Upload failed'); }
    e.target.value = '';
  };

  const handleDownload = async (template = false) => {
    const url = template ? '/clients-registry/csv-template' : `/clients-registry/download-csv?division_id=${divisionId}`;
    const r = await api.get(url, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = template ? 'clients_template.csv' : `clients_${divisionId}.csv`; a.click();
  };

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading clients...</div>;

  const riskPalette = {
    green: { bg: '#ECFDF5', text: '#065F46', accent: '#2D8B5E', label: 'Healthy spread' },
    amber: { bg: '#FEF3C7', text: '#92400E', accent: '#BA7517', label: 'Watch — concentrated' },
    red:   { bg: '#FEF2F2', text: '#991B1B', accent: '#D4726A', label: 'High concentration risk' },
  };

  return (
    <div className="space-y-4" data-testid="client-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Client Relationships ({clients.length})</h4>
        <div className="flex items-center gap-2">
          <button onClick={() => handleDownload(true)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="csv-template-btn"><FileSpreadsheet size={12} /> Template</button>
          <button onClick={() => handleDownload(false)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="csv-download-btn"><Download size={12} /> Export</button>
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="csv-upload-btn"><Upload size={12} /> Import CSV</button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-client-btn"><Plus size={13} /> Add Client</button>
        </div>
      </div>

      {/* Concentration / single-point-of-failure widget */}
      {concentration && concentration.totalClients >= 2 && (() => {
        const p = riskPalette[concentration.risk];
        return (
          <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: p.accent + '66', backgroundColor: p.bg }} data-testid="client-concentration-widget">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'white' }}>
                  {concentration.risk === 'green'
                    ? <TrendingUp size={16} style={{ color: p.accent }} />
                    : <AlertTriangle size={16} style={{ color: p.accent }} />}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: p.text }}>
                    {p.label}
                  </p>
                  <p className="text-sm font-bold mt-0.5" style={{ color: '#1B2A4A' }} data-testid="top5-line">
                    Top 5 clients = <span style={{ color: p.accent }}>{concentration.top5Pct}%</span> of book
                    <span className="text-xs font-normal ml-1.5" style={{ color: '#6B7280' }}>
                      (₹{(concentration.top5Sum / 10000000).toFixed(2)}Cr of ₹{(concentration.total / 10000000).toFixed(2)}Cr)
                    </span>
                  </p>
                  {concentration.topOne && concentration.topOnePct >= 20 && (
                    <p className="text-xs mt-1" style={{ color: p.text }} data-testid="top1-line">
                      <span className="font-semibold">{concentration.topOne.name}</span> alone =
                      <span className="font-semibold ml-1">{concentration.topOnePct}%</span> —
                      <span className="ml-1">losing them would cost ₹{(concentration.topOne.value / 10000000).toFixed(2)}Cr/year</span>
                    </p>
                  )}
                </div>
              </div>
              {/* Top-5 mini ranking */}
              <div className="flex items-end gap-1.5 flex-shrink-0" aria-hidden="true">
                {concentration.top5.map((c, i) => {
                  const h = Math.max(14, Math.round((c.value / concentration.top5[0].value) * 40));
                  return (
                    <div key={c.name} className="flex flex-col items-center gap-1" title={`${c.name}: ${fmt(c.value)}`}>
                      <div className="rounded-sm" style={{ width: 14, height: h, backgroundColor: i === 0 ? p.accent : p.accent + '88' }} />
                      <span className="text-[9px]" style={{ color: p.text, maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name.split(' ')[0].slice(0, 6)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="client-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.client_name} onChange={e => setForm(p => ({...p, client_name: e.target.value}))} placeholder="Client name *" required className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="client-name-input" />
            <input value={form.entity} onChange={e => setForm(p => ({...p, entity: e.target.value}))} placeholder="Entity (CC/ME/LC)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input type="date" value={form.relationship_start_date} onChange={e => setForm(p => ({...p, relationship_start_date: e.target.value}))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} title="Relationship start date" />
            <input type="number" value={form.annual_value} onChange={e => setForm(p => ({...p, annual_value: e.target.value}))} placeholder="Annual value (INR)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.services} onChange={e => setForm(p => ({...p, services: e.target.value}))} placeholder="Services (Concierge, Clubs...)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.relationship_owner} onChange={e => setForm(p => ({...p, relationship_owner: e.target.value}))} placeholder="Relationship owner" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input type="number" value={form.nps_score} onChange={e => setForm(p => ({...p, nps_score: e.target.value}))} placeholder="NPS score (1-10)" min="1" max="10" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <select value={form.satisfaction_indicator} onChange={e => setForm(p => ({...p, satisfaction_indicator: e.target.value}))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
              {HEALTH.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <input type="date" value={form.last_meeting_date} onChange={e => setForm(p => ({...p, last_meeting_date: e.target.value}))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} title="Last meeting date" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.key_contact_name} onChange={e => setForm(p => ({...p, key_contact_name: e.target.value}))} placeholder="Key contact name" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.key_contact_role} onChange={e => setForm(p => ({...p, key_contact_role: e.target.value}))} placeholder="Key contact role" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.how_they_found_us} onChange={e => setForm(p => ({...p, how_they_found_us: e.target.value}))} placeholder="How they found us" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <textarea value={form.why_they_stay} onChange={e => setForm(p => ({...p, why_they_stay: e.target.value}))} placeholder="Why they stay" rows={2} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Notes" rows={2} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="client-submit">{editId ? 'Update' : 'Add Client'}</button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Client</th>
              <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Value</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Services</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>NPS</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Health</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Since</th>
              <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {clients.map(c => {
                const hasBreakdown = Array.isArray(c.contracts) && c.contracts.length > 1;
                const isOpen = !!expanded[c.id];
                return (
                  <Fragment key={c.id}>
                    <tr data-testid={`client-row-${c.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {hasBreakdown ? (
                            <button
                              onClick={() => setExpanded(p => ({ ...p, [c.id]: !p[c.id] }))}
                              className="p-0.5 hover:bg-gray-100 rounded"
                              data-testid={`expand-${c.id}`}
                              title={isOpen ? 'Hide contracts' : 'Show contracts'}
                            >
                              {isOpen ? <ChevronDown size={13} style={{ color: '#6B7280' }} /> : <ChevronRight size={13} style={{ color: '#6B7280' }} />}
                            </button>
                          ) : <span className="inline-block w-[17px]" />}
                          <div>
                            <p className="font-medium" style={{ color: '#1B2A4A' }}>
                              {c.client_name}
                              {hasBreakdown && (
                                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#EEF2FF', color: '#1B2A4A' }}>
                                  {c.contracts.length} contracts
                                </span>
                              )}
                            </p>
                            <p className="text-xs" style={{ color: '#9CA3AF' }}>{c.relationship_owner}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right" style={{ color: '#1B2A4A' }}>{fmt(c.annual_value)}</td>
                      <td className="px-3 py-2.5 hidden sm:table-cell text-xs" style={{ color: '#6B7280' }}>{c.services}</td>
                      <td className="px-3 py-2.5 text-center" style={{ color: '#1B2A4A' }}>{c.nps_score || '-'}</td>
                      <td className="px-3 py-2.5 text-center">{healthDot(c.satisfaction_indicator)}</td>
                      <td className="px-3 py-2.5 hidden md:table-cell text-xs" style={{ color: '#6B7280' }}>{c.relationship_start_date || '-'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => navigate(`/client/${c.id}`)} className="p-1 text-xs hover:bg-gray-100 rounded inline-flex items-center gap-1" style={{ color: '#1B2A4A' }} data-testid={`open-dossier-${c.id}`} title="Open full dossier">
                          <FolderOpen size={12} /> Dossier
                        </button>
                        <button onClick={() => handleEdit(c)} className="p-1 text-xs hover:bg-gray-100 rounded" style={{ color: '#2A7B88' }}>Edit</button>
                        <button onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/clients-registry/${c.id}`); refresh(); }}} className="p-1 hover:bg-gray-100 rounded"><Trash2 size={13} style={{ color: '#D4726A' }} /></button>
                      </td>
                    </tr>
                    {hasBreakdown && isOpen && (
                      <tr data-testid={`breakdown-${c.id}`}>
                        <td colSpan={7} className="px-3 py-2" style={{ backgroundColor: '#FAF9F7' }}>
                          <div className="pl-6">
                            <p className="text-[10px] font-semibold uppercase mb-1.5" style={{ color: '#9CA3AF' }}>Contract breakdown</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr style={{ color: '#9CA3AF' }}>
                                  <th className="text-left py-1 font-medium">Service line</th>
                                  <th className="text-right py-1 font-medium">Annual</th>
                                  <th className="text-right py-1 font-medium hidden sm:table-cell">Monthly</th>
                                  <th className="text-left py-1 font-medium hidden md:table-cell">End date</th>
                                  <th className="text-left py-1 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.contracts.map((ct, idx) => (
                                  <tr key={idx} style={{ color: '#1B2A4A' }}>
                                    <td className="py-1">{ct.entity || '—'}</td>
                                    <td className="py-1 text-right">{fmt(ct.annual_value)}</td>
                                    <td className="py-1 text-right hidden sm:table-cell">{ct.fixed_monthly ? fmt(ct.fixed_monthly) : '—'}</td>
                                    <td className="py-1 hidden md:table-cell" style={{ color: '#6B7280' }}>{ct.end_date || 'Open'}</td>
                                    <td className="py-1">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: ct.renewal_status === 'renewing' ? '#FEF3C7' : '#ECFDF5', color: ct.renewal_status === 'renewing' ? '#92400E' : '#065F46' }}>
                                        {ct.renewal_status || 'active'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {clients.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No clients yet. Add your first client or import via CSV.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
